import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";

/* ---------- Typographic primitives ---------- */

const P = ({ children }) => <p className="text-[#4A4A52] leading-relaxed mb-4">{children}</p>;

const A = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-[#D32F2F] hover:underline break-words"
  >
    {children}
  </a>
);

const DashList = ({ items }) => (
  <ul className="space-y-2.5 mb-4 text-[#4A4A52] leading-relaxed">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-[0.65em] w-3 h-px bg-[#D32F2F] shrink-0" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const LetterList = ({ items }) => (
  <ul className="space-y-2.5 mb-4 text-[#4A4A52] leading-relaxed">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3">
        <span className="font-display font-semibold text-[#D32F2F] shrink-0" aria-hidden="true">
          {String.fromCharCode(97 + i)})
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/* ---------- Editorial chapter (numbered manifesto style) ---------- */

const Chapter = ({ n, title, children }) => (
  <Reveal
    className="grid grid-cols-1 md:grid-cols-[96px_1fr] gap-2 md:gap-8 py-10 lg:py-12 border-t border-[#E5E0D8] first:border-t-0 first:pt-0"
  >
    <div data-testid={`datenschutz-chapter-${n}`} className="md:pt-1 select-none" aria-hidden="true">
      <span className="font-display text-4xl lg:text-5xl font-bold text-[#D7C4B1] leading-none">
        {n}
      </span>
    </div>
    <div>
      <h2 className="font-display text-xl lg:text-2xl font-bold text-[#2B2B30] mb-5">{title}</h2>
      {children}
    </div>
  </Reveal>
);

/* ---------- Masked line reveal (signature on-load moment) ---------- */

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.21, 0.6, 0.35, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

/* ---------- Page ---------- */

export default function Datenschutz() {
  useSeo(
    "Datenschutzerklärung der Fussboden Blum GmbH",
    "Datenschutzerklärung der Fussboden Blum GmbH"
  );

  return (
    <div data-testid="datenschutz-page">
      <Navbar />
      <main>
        {/* Header band */}
        <section className="wood-band text-white pt-40 pb-16">
          <div className="container-blum">
            <MaskedLine delay={0.1}>
              <p className="overline text-[#D7C4B1]">Rechtliches</p>
            </MaskedLine>
            <h1
              data-testid="datenschutz-hero-title"
              className="text-4xl sm:text-5xl font-bold tracking-tight mt-4"
            >
              <MaskedLine delay={0.25}>Datenschutz&shy;erklärung</MaskedLine>
            </h1>
            <MaskedLine delay={0.45}>
              <p className="text-white/60 mt-6 max-w-xl leading-relaxed">
                Informationen zur Erhebung und Verwendung personenbezogener Daten
                gemäß DSGVO, BDSG und TMG.
              </p>
            </MaskedLine>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 lg:py-24">
          <div className="container-blum max-w-4xl">
            <Chapter n="01" title="Geltungsbereich der Datenschutzerklärung">
              <P>
                Diese Datenschutzerklärung soll die Nutzer dieser Website gemäß
                Bundesdatenschutzgesetz und Telemediengesetz über die Art, den Umfang und
                den Zweck der Erhebung und Verwendung personenbezogener Daten durch den
                Websitebetreiber informieren:
              </P>
              <div className="border-l-2 border-[#D32F2F] pl-5 mb-6 text-[#2B2B30] leading-relaxed">
                <p className="font-display font-semibold">Fußboden Blum GmbH</p>
                <p>Gewerbestraße 15, 36148 Kalbach</p>
                <p>Tel. 06655-916190</p>
                <p>
                  E-Mail:{" "}
                  <a href="mailto:info@fussboden-blum.de" className="text-[#D32F2F] hover:underline">
                    info@fussboden-blum.de
                  </a>
                </p>
                <p>
                  Internet:{" "}
                  <A href="https://www.fussboden-blum.de">www.fussboden-blum.de</A>
                </p>
              </div>
              <P>
                Der Websitebetreiber nimmt Ihren Datenschutz sehr ernst und behandelt Ihre
                personenbezogenen Daten vertraulich und entsprechend der gesetzlichen
                Vorschriften.
              </P>
              <P>
                Die Speicherung Ihrer persönlichen Daten, die Sie auf eigenen Wunsch über die
                Kontaktseite eingeben können, werden direkt an uns weitergeleitet und zu keinen
                weiteren Zwecken gespeichert.
              </P>
              <P>
                Bedenken Sie, dass die Datenübertragung im Internet grundsätzlich mit
                Sicherheitslücken bedacht sein kann. Ein vollumfänglicher Schutz vor dem Zugriff
                durch Fremde ist nicht realisierbar.
              </P>
            </Chapter>

            <Chapter n="02" title="Informationen zur Datenerhebung gemäß Artikel 13 DSGVO">
              <P>
                Die Firma Fußboden Blum GmbH, Gewerbestraße 15, 36148 Kalbach, Geschäftsführer
                Herr Michael Blum, erhebt Ihre Daten zum Zweck der Vertragsdurchführung, zur
                Erfüllung Ihrer vertraglichen und vorvertraglichen Pflichten sowie zur
                Direktwerbung.
              </P>
              <P>
                Die Datenerhebung und Datenverarbeitung ist für die Durchführung des Vertrags
                erforderlich und beruht auf Art. 6 Abs. 1 b) DSGVO. Eine Weitergabe der Daten an
                Dritte erfolgt ausschließlich zur Abwicklung des Auftrags. Eine Auftragserteilung
                ist nicht zwingend an die Zustimmung einer Direktwerbung gebunden.
              </P>
              <P>
                Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben,
                jederzeit gegen die Verarbeitung Sie betreffender personenbezogener Daten
                Widerspruch einzulegen sowie dem Zweck der Direktwerbung jederzeit zu
                widersprechen. Wir verarbeiten die personenbezogenen Daten dann nicht mehr, es sei
                denn, es liegen nachweisbar zwingende schutzwürdige Gründe für die Verarbeitung
                vor, die Interessen, Rechte und Freiheiten der betroffenen Person überwiegen, oder
                die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von
                Rechtsansprüchen.
              </P>
              <P>
                Zudem sind Sie jederzeit berechtigt, kostenlos Auskunft der bei uns über Sie
                gespeicherten Daten zu beantragen sowie die Berichtigung, Löschung und Sperrung
                einzelner personenbezogener Daten zu verlangen. Sie können den Widerruf entweder
                schriftlich per Post, per E-Mail oder Fax an die Firma Fußboden Blum GmbH
                übermitteln. Ihnen steht ein Recht auf Beschwerde bei der zuständigen
                Aufsichtsbehörde zu, wenn Sie der Ansicht sind, dass die Verarbeitung der Sie
                betreffenden Daten gegen datenschutzrechtliche Bestimmungen verstößt.
              </P>
            </Chapter>

            <Chapter n="03" title="Zugriffsdaten">
              <P>
                Der Websitebetreiber bzw. Seitenprovider erhebt Daten über Zugriffe auf die Seite
                und speichert diese als „Server-Logfiles" ab. Folgende Daten werden so
                protokolliert:
              </P>
              <DashList
                items={[
                  "Besuchte Website",
                  "Datum und Uhrzeit zum Zeitpunkt des Zugriffes",
                  "Menge der gesendeten Daten in Byte",
                  "Quelle/Verweis, von welchem Sie auf die Seite gelangten",
                  "Verwendeter Browser nebst Version",
                  "Verwendetes Betriebssystem",
                  "Verwendete IP-Adresse",
                ]}
              />
              <P>
                Die erhobenen Daten dienen lediglich statistischen Auswertungen und zur
                Verbesserung sowie Bereitstellung der Website. Der Websitebetreiber behält sich
                allerdings vor, die Server-Logfiles nachträglich zu überprüfen, sollten konkrete
                Anhaltspunkte auf eine rechtswidrige Nutzung hinweisen.
              </P>
            </Chapter>

            <Chapter n="04" title="Weitergabe von Daten">
              <P>
                Eine Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den im
                Folgenden aufgeführten Zwecken findet nicht statt. Wir geben Ihre persönlichen
                Daten nur an Dritte weiter, wenn:
              </P>
              <LetterList
                items={[
                  "Sie Ihre nach Art. 6 Abs. 1 S. 1 lit. a DSGVO ausdrückliche Einwilligung dazu erteilt haben,",
                  "für den Fall, dass für die Weitergabe nach Art. 6 Abs. 1 S. 1 lit. c DSGVO eine gesetzliche Verpflichtung besteht,",
                  "die Weitergabe nach Art. 6 Abs. 1 S. 1 lit. f DSGVO zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist und kein Grund zur Annahme besteht, dass Sie ein überwiegendes schutzwürdiges Interesse an der Nichtweitergabe Ihrer Daten haben,",
                  "dies gesetzlich zulässig und nach Art. 6 Abs. 1 S. 1 lit. b DSGVO für die Abwicklung von Vertragsverhältnissen mit Ihnen erforderlich ist.",
                ]}
              />
            </Chapter>

            <Chapter n="05" title="Cookies">
              <P>
                Diese Website verwendet Cookies. Dabei handelt es sich um winzige Textdateien, die
                im Speicher Ihres Computers abgelegt werden und die Kommunikation zwischen Browser
                und Server optimieren. Sie ermöglichen uns dadurch, Sie bei Ihrem nächsten Besuch
                wiedererkennen zu können. Nach dem Ende der Browser-Sitzung werden die meisten der
                von uns verwendeten Cookies wieder von Ihrem Computer entfernt
                („Sitzungs-Cookies"). Die sogenannten „dauerhaften Cookies" verbleiben hingegen auf
                dem Rechner. Cookies richten auf Ihrem Endgerät keinen Schaden an, enthalten keine
                Viren, Trojaner oder sonstige Schadsoftware. Über die Einstellungen Ihres Browsers
                können Sie den Einsatz, die Speicherung und auch die Verweigerung der Annahme von
                Cookie-Dateien einstellen und konfigurieren. Nähere Informationen hierzu finden Sie
                in der Bedienungsanleitung Ihres Browsers.
              </P>
              <P>
                Zweck des Einsatzes dieser Technologie ist es, die Navigation und
                Benutzerfreundlichkeit zu erleichtern. Es kann das Nutzerverhalten verfolgt und
                damit überprüft werden, ob der Internetauftritt im beabsichtigten Maße genutzt wird
                und keine unnötigen Barrieren bestehen, die ihren Grund in der Gestaltung unserer
                Website haben. So ermöglichen Sie durch Ihr Nutzerverhalten eine ständige Kontrolle
                und Verbesserung der Usability von „fussboden-blum.de". Zum anderen setzen wir
                Cookies ein, um die Nutzung unserer Website statistisch zu erfassen und zum Zwecke
                der Optimierung unseres Angebotes für Sie auszuwerten. Den Cookie-Umfang können Sie
                zudem beim Aufrufen der Seite selbst festlegen.
              </P>
            </Chapter>

            <Chapter n="06" title="Umgang mit Kontaktdaten">
              <P>
                Nehmen Sie mit dem Websitebetreiber durch die angebotenen Kontaktmöglichkeiten
                Verbindung auf, werden Ihre Angaben gespeichert, damit auf diese zur Bearbeitung
                und Beantwortung Ihrer Anfrage zurückgegriffen werden kann. Ohne Ihre explizite
                Einwilligung werden diese Daten nicht an Dritte weitergegeben. Die uns auf diese
                Weise übermittelten personenbezogenen Daten werden wir selbstverständlich
                ausschließlich für den Zweck verwenden, zu dem Sie uns diese bei der Kontaktierung
                zur Verfügung stellen. Eine Mitteilung dieser Angaben erfolgt ausdrücklich auf
                freiwilliger Basis und mit Ihrer Einwilligung. Rechtsgrundlage zur
                Datenverarbeitung ist damit Art. 6 Abs. 1 S. 1 lit. a DSGVO.
              </P>
            </Chapter>

            <Chapter n="07" title="Umgang mit Kommentaren und Beiträgen">
              <P>
                Die Kommentar- und Beitragsfunktion ist deaktiviert. Sollte es aufgrund von
                Fehleinstellungen möglich sein, trotzdem Beiträge oder Kommentare zu hinterlassen,
                wird Ihre IP-Adresse gespeichert. Dies dient der Sicherheit des Websitebetreibers:
                Verstößt Ihr Text gegen das Recht, möchte er Ihre Identität nachverfolgen können.
              </P>
            </Chapter>

            <Chapter n="08" title="Google Maps">
              <P>
                Diese Webseite verwendet Funktionen von Google Maps, d. h. die Google Maps API,
                einen Dienst der Google Inc., 1600 Amphitheatre Parkway, Mountain View, CA 94043,
                USA, um geographische Informationen visuell darzustellen. Bei der Nutzung von
                Google Maps werden von Google auch Daten über die Nutzung der Kartenfunktionen
                durch Besucher erhoben, verarbeitet und genutzt. Nähere Informationen über die
                Datenverarbeitung durch Google können Sie den{" "}
                <A href="http://www.google.de/intl/de/policies/privacy">Google-Datenschutzhinweisen</A>{" "}
                entnehmen. Dort können Sie im Datenschutzcenter auch Ihre persönlichen
                Datenschutz-Einstellungen anpassen und verwalten. Dies erfolgt unabhängig davon, ob
                Google ein Nutzerkonto bereitstellt, über das Sie eingeloggt sind, oder ob kein
                Nutzerkonto besteht. Wenn Sie bei Google eingeloggt sind, werden Ihre Daten direkt
                Ihrem Konto zugeordnet. Wenn Sie diese Zuordnung nicht wünschen, müssen Sie sich
                entweder ausloggen oder Ihre Einstellungen im Google-Konto anpassen.
              </P>
              <P>
                Weitere Informationen zu Zweck und Umfang der Datenerhebung und ihrer Verarbeitung
                durch den Plug-in-Anbieter erhalten Sie in den Datenschutzerklärungen des
                Anbieters. Dort erhalten Sie auch weitere Informationen zu Ihren diesbezüglichen
                Rechten und Einstellungsmöglichkeiten zum Schutze Ihrer Privatsphäre:{" "}
                <A href="http://www.google.de/intl/de/policies/privacy">
                  http://www.google.de/intl/de/policies/privacy
                </A>
                . Google verarbeitet Ihre personenbezogenen Daten auch in den USA und hat sich dem
                EU-US Privacy Shield unterworfen,{" "}
                <A href="https://www.privacyshield.gov/EU-US-Framework">
                  https://www.privacyshield.gov/EU-US-Framework
                </A>
                .
              </P>
              <P>
                Die Nutzung der Google Maps API hat zur Folge, dass auf unserer Seite Java-Script
                Code der Google Inc., 1600 Amphitheatre Parkway, Mountain View, CA 94043
                (nachfolgend: Google) nachgeladen wird. Dadurch wird die Übermittlung von Daten an
                Google ermöglicht. Welche Daten dies im Einzelnen sind, ist uns nicht bekannt. Jene
                Verarbeitung unterliegt der Verantwortung von Google. Sie können in Ihrem Browser
                Java-Script deaktivieren oder einen Java-Script-Blocker (zum Beispiel{" "}
                <A href="https://www.noscript.net">www.noscript.net</A>) installieren, um dies zu
                verhindern.
              </P>
            </Chapter>

            <Chapter n="09" title="etracker Analyse">
              <P>
                Der Anbieter dieser Website nutzt Dienste der etracker GmbH aus Hamburg,
                Deutschland (<A href="https://www.etracker.com">www.etracker.com</A>) zur Analyse
                von Nutzungsdaten. Wir verwenden standardmäßig keine Cookies für die Web-Analyse.
                Soweit wir Analyse- und Optimierungs-Cookies einsetzen, holen wir Ihre explizite
                Einwilligung gesondert im Vorfeld ein. Ist das der Fall und Sie stimmen zu, werden
                Cookies eingesetzt, die eine statistische Reichweiten-Analyse dieser Website, eine
                Erfolgsmessung unserer Online-Marketing-Maßnahmen sowie Testverfahren ermöglichen,
                um z. B. unterschiedliche Versionen unseres Online-Angebotes oder seiner
                Bestandteile zu testen und zu optimieren. Cookies sind kleine Textdateien, die vom
                Internet Browser auf dem Endgerät des Nutzers gespeichert werden. etracker Cookies
                enthalten keine Informationen, die eine Identifikation eines Nutzers ermöglichen.
              </P>
              <P>
                Die mit etracker erzeugten Daten werden im Auftrag des Anbieters dieser Website von
                etracker ausschließlich in Deutschland verarbeitet und gespeichert und unterliegen
                damit den strengen deutschen und europäischen Datenschutzgesetzen und -standards.
                etracker wurde diesbezüglich unabhängig geprüft, zertifiziert und mit dem
                Datenschutz-Gütesiegel ePrivacyseal ausgezeichnet.
              </P>
              <P>
                Die Datenverarbeitung erfolgt auf Basis der gesetzlichen Bestimmungen des Art. 6
                Abs. 1 lit. f (berechtigtes Interesse) der Datenschutzgrundverordnung (DSGVO).
                Unser Anliegen im Sinne der DSGVO (berechtigtes Interesse) ist die Optimierung
                unseres Online-Angebotes und unseres Webauftritts. Da uns die Privatsphäre unserer
                Besucher wichtig ist, werden die Daten, die möglicherweise einen Bezug zu einer
                einzelnen Person zulassen, wie die IP-Adresse, Anmelde- oder Gerätekennungen,
                frühestmöglich anonymisiert oder pseudonymisiert. Eine andere Verwendung,
                Zusammenführung mit anderen Daten oder eine Weitergabe an Dritte erfolgt nicht.
              </P>
              <P>
                Sie können der vorbeschriebenen Datenverarbeitung jederzeit widersprechen. Der
                Widerspruch hat keine nachteiligen Folgen. Weitere Informationen zum Datenschutz
                bei etracker finden Sie{" "}
                <A href="https://www.etracker.com/datenschutz/">hier</A>.
              </P>
            </Chapter>

            <Chapter n="10" title="Nutzung von Social-Media-Plugins">
              <P>
                Diese Website verwendet Facebook Social Plugins, welches von der Facebook Inc. (1
                Hacker Way, Menlo Park, California 94025, USA) betrieben wird. Erkennbar sind die
                Einbindungen an dem Facebook-Logo bzw. an den Begriffen „Like", „Gefällt mir",
                „Teilen" in den Farben Facebooks (Blau und Weiß). Informationen zu allen
                Facebook-Plugins finden Sie im folgenden Link:{" "}
                <A href="https://developers.facebook.com/docs/plugins/">
                  https://developers.facebook.com/docs/plugins/
                </A>
                . Das Plugin stellt eine direkte Verbindung zwischen Ihrem Browser und den Facebook
                Servern her. Der Websitebetreiber hat keinerlei Einfluss auf die Natur und den
                Umfang der Daten, welche das Plugin an die Server der Facebook Inc. übermittelt.
                Informationen dazu finden Sie hier:{" "}
                <A href="https://www.facebook.com/help/186325668085084">
                  https://www.facebook.com/help/186325668085084
                </A>
                . Das Plugin informiert die Facebook Inc. darüber, dass Sie Nutzer diese Website
                besucht hat. Es besteht hierbei die Möglichkeit, dass Ihre IP-Adresse gespeichert
                wird. Sind Sie während des Besuchs auf dieser Website in Ihrem Facebook-Konto
                eingeloggt, werden die genannten Informationen mit diesem verknüpft.
              </P>
              <P>
                Nutzen Sie die Funktionen des Plugins – etwa indem Sie einen Beitrag teilen oder
                „liken" – werden die entsprechenden Informationen ebenfalls an die Facebook Inc.
                übermittelt. Möchten Sie verhindern, dass die Facebook Inc. diese Daten mit Ihrem
                Facebook-Konto verknüpft, loggen Sie sich bitte vor dem Besuch dieser Website bei
                Facebook aus.
              </P>
              <P>
                Fussboden Blum greift für diese Website auf die technische Plattform und die
                Dienste von Instagram zurück. Der Instagram-Service ist eines der
                Facebook-Produkte, die von Facebook Ireland Limited („Facebook") zur Verfügung
                gestellt werden. Instagram ist eine Online-Plattform, die das Teilen von Fotos und
                Videos ermöglicht. Nutzer können Fotos und Videos bearbeiten und mit Filtern
                versehen. Andere Nutzer können die Fotos und Videos wiederum teilen, kommentieren
                oder als „Gefällt mir" markieren. Außerdem können Nutzer miteinander in Kontakt
                treten, indem sie private Nachrichten schreiben.
              </P>
            </Chapter>

            <Chapter n="11" title="Nutzung von Meta Lead Ads (Instant Forms)">
              <P>
                Auf dieser Website werden Werbeanzeigen über die Plattform Meta (Facebook und
                Instagram) geschaltet. Im Rahmen dieser Anzeigenkampagnen setzen wir sogenannte
                Meta Instant Forms (auch Lead Ads genannt) ein. Diese ermöglichen es
                Interessentinnen und Interessenten, direkt innerhalb der Meta-Plattform eine
                Kontaktanfrage zu stellen, ohne die jeweilige Unterseite unserer Website
                aufzurufen.
              </P>
              <P>
                Die Anzeigen werden in unserem Auftrag geschaltet. Für die technische Durchführung
                und Datenverwaltung innerhalb der Meta-Plattform ist Meta Platforms Ireland Limited
                (4 Grand Canal Square, Dublin 2, Irland) verantwortlich. Wir erhalten die über die
                Formulare übermittelten Kontaktdaten zur Bearbeitung der jeweiligen Anfrage.
              </P>
              <P>Welche Daten werden dabei erfasst?</P>
              <DashList
                items={[
                  "Name (Vorname, Nachname)",
                  "E-Mail-Adresse",
                  "Telefonnummer (sofern angegeben)",
                  "Inhalt der Anfrage (z. B. gewählte Leistung, Projektzeitraum)",
                ]}
              />
              <P>
                Einige dieser Felder werden von Meta aus dem Nutzerprofil der anfragenden Person
                vorausgefüllt, sofern entsprechende Daten im Profil hinterlegt sind. Die Angaben
                können vor dem Absenden geprüft und angepasst werden.
              </P>
              <P>
                Die erhobenen Daten werden ausschließlich zur Bearbeitung und Beantwortung der
                eingegangenen Kontaktanfragen genutzt. Eine Weitergabe an Dritte erfolgt nicht, es
                sei denn, dies ist zur Bearbeitung der Anfrage erforderlich oder gesetzlich
                vorgeschrieben. Die Verarbeitung der übermittelten Kontaktdaten erfolgt auf
                Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung) sowie Art. 6 Abs. 1
                lit. f DSGVO (berechtigtes Interesse an der Bearbeitung von Kundenanfragen).
              </P>
              <P>
                Die über Meta Instant Forms übermittelten Daten werden gelöscht, sobald der Zweck
                der Kontaktaufnahme erfüllt ist und keine gesetzlichen Aufbewahrungspflichten
                entgegenstehen. In der Regel erfolgt dies spätestens nach Abschluss des jeweiligen
                Vorgangs.
              </P>
              <P>
                Im Rahmen der Nutzung von Meta Lead Ads findet eine eigenständige Datenverarbeitung
                durch Meta Platforms Ireland Limited statt. Meta kann dabei im Rahmen des Betriebs
                seiner Plattform eigene Daten erheben und verarbeiten. Für diese Verarbeitung ist
                allein Meta verantwortlich. Informationen zur Datenverarbeitung durch Meta finden
                Sie in der{" "}
                <A href="https://www.facebook.com/privacy/policy/">
                  Datenschutzrichtlinie von Meta
                </A>
                . Sie haben das Recht, der Verarbeitung Ihrer Daten jederzeit zu widersprechen. Zur
                Ausübung Ihrer Rechte (Auskunft, Berichtigung, Löschung, Einschränkung der
                Verarbeitung, Datenübertragbarkeit) wenden Sie sich bitte an uns über die auf
                dieser Website angegebenen Kontaktdaten.
              </P>
            </Chapter>

            <Chapter n="12" title="Newsletter-Abonnement">
              <P>
                Der Websitebetreiber bietet derzeit keine Newsletterfunktion an. Sollten Sie über
                verlinkte Seiteninhalte das Angebot eines Newsletters anfragen, dann ist dafür
                alleine der Websitebetreiber verantwortlich, der den Newsletter anbietet.
                Grundsätzlich gilt: Möchten Sie den Newsletter abonnieren, müssen Sie eine valide
                E-Mail-Adresse angeben.
              </P>
            </Chapter>

            <Chapter n="13" title="Widerspruchsrecht">
              <P>
                Werden Daten auf Grundlage von Art. 6 Abs. 1 e oder f erhoben (Datenverarbeitung
                zur Wahrung berechtigter Interessen), steht Ihnen das Recht zu, aus Gründen, die
                sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung
                Widerspruch einzulegen. Wir verarbeiten die personenbezogenen Daten dann nicht
                mehr, es sei denn, es liegen nachweisbar zwingende schutzwürdige Gründe für die
                Verarbeitung vor, die die Interessen, Rechte und Freiheiten der betroffenen Person
                überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder
                Verteidigung von Rechtsansprüchen.
              </P>
              <P>
                Werden personenbezogene Daten bearbeitet, um Direktwerbung zu betreiben, so hat die
                betroffene Person das Recht, jederzeit Widerspruch gegen die Verarbeitung der
                personenbezogenen Daten zum Zwecke derartiger Werbung einzulegen. Widerspricht die
                betroffene Person der Verarbeitung für Zwecke der Direktwerbung, so werden die
                personenbezogenen Daten nicht mehr für diese Zwecke verarbeitet.
              </P>
            </Chapter>

            <Chapter n="14" title="Recht auf Widerruf einer datenschutzrechtlichen Einwilligung">
              <P>
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung
                möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu
                reicht eine formlose Mitteilung per E-Mail an uns. Die Rechtmäßigkeit der bis zum
                Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
              </P>
            </Chapter>

            <Chapter n="15" title="Nutzung von CleanTalk Antispam">
              <P>
                Wir nutzen den Dienst „CleanTalk", der die Webseite vor Spam schützt. Die Nutzung
                erfolgt auf Grundlage unserer berechtigten Interessen im Sinne des Art. 6 Abs. 1
                lit. f) Datenschutzgrundverordnung (DSGVO). Aus Sicherheitsgründen und als Schutz
                vor Spam werden Ihre Daten im CleanTalk Cloud Service verarbeitet und in Logfiles
                für maximal 45 Tage gespeichert. Nach Ablauf der genannten Frist werden diese Daten
                vollständig gelöscht. CleanTalk kann Informationen über die Spam-Aktivität von IP-
                oder E-Mail-Adressen verwenden, um allen mit seinem Dienst verbundenen Websites
                einen angemessenen Anti-Spam-Schutz zu bieten. Nähere Informationen zur Erhebung
                und Nutzung der Daten durch CleanTalk finden sich in den Datenschutzhinweisen von
                CleanTalk Inc.:{" "}
                <A href="https://cleantalk.org/publicoffer#privacy">
                  https://cleantalk.org/publicoffer#privacy
                </A>
              </P>
            </Chapter>

            <Chapter n="16" title="Auskunft, Berichtigung, Sperrung und Löschung">
              <P>
                Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf
                unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren
                Herkunft und Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht auf
                Berichtigung, Sperrung oder Löschung dieser Daten. Hierzu sowie zu weiteren Fragen
                zum Thema personenbezogener Daten können Sie sich jederzeit unter der im{" "}
                <Link to="/impressum" className="text-[#D32F2F] hover:underline">
                  Impressum
                </Link>{" "}
                angegebenen Adresse an uns wenden – oder per Mail an{" "}
                <a href="mailto:info@fussboden-blum.de" className="text-[#D32F2F] hover:underline">
                  info@fussboden-blum.de
                </a>
                .
              </P>
            </Chapter>

            <Chapter n="17" title="Beschwerderecht bei einer Aufsichtsbehörde">
              <P>
                Sie haben gem. Art. 77 DSGVO das Recht auf Beschwerde bei einer Aufsichtsbehörde,
                wenn Sie der Ansicht sind, dass die Verarbeitung der Sie betreffenden Daten gegen
                datenschutzrechtliche Bestimmungen verstößt. Das Beschwerderecht kann insbesondere
                bei einer Aufsichtsbehörde in dem Mitgliedstaat Ihres Aufenthaltsorts, Ihres
                Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes geltend gemacht werden.
              </P>
            </Chapter>

            {/* Closing note */}
            <Reveal className="mt-4 bg-[#F7F4EF] border-l-2 border-[#D32F2F] p-6 lg:p-8">
              <P>
                Fussboden Blum ist bemüht, Ihre personenbezogenen Daten durch Ergreifung aller
                relevanten technischen und organisatorischen Möglichkeiten (kurz: TOM) so zu
                verarbeiten und zu speichern, dass sie für Dritte nicht zugänglich sind. So werden
                Ihre Daten in einer sicheren IT-Umgebung verarbeitet. Für die Datenübermittlung
                setzen wir abgesicherte Server mit SSL-Technik (Secure Socket Layer) ein. So werden
                Ihre Daten sicher und für Unbefugte unlesbar an uns übermittelt.
              </P>
              <p className="text-[#2B2B30] font-medium leading-relaxed">
                Wir behalten uns das Recht vor, die Datenschutzerklärung jederzeit unter Beachtung
                des geltenden Rechts anzupassen und zu ändern.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
